import type { AxiosResponse } from 'axios'
import type { SelectOption, DestroySelectOption, TemplateVariable } from '@/questionnaire/types'
import type { ApiStep, ApiQuestion, Template } from '../types'
import httpClient from '@/utils/httpClient'

export type AiRunResult = {
  id: string
  status: string
  template_id: string
  error?: string
}

export const createTemplate = async (template: Omit<Template, 'id' | 'created_at' | 'updated_at'>): Promise<Template> => {
  const res = await httpClient.post<Template>('/v1/templates', { template })
  return res.data
}

export const updateTemplate = async (template: Template): Promise<Template> => {
  if (!template.id) throw "No template ID set - can't update"
  const res = await httpClient.put<{ template: Template }>(`/v1/templates/${template.id}`, { template })
  return res.data.template
}

export const getTemplate = async (templateID: string): Promise<Template> => {
  const res = await httpClient.get<{ template: Template }>(`/v1/templates/${templateID}`)
  return res.data.template
}

export const getTemplates = async (archived: boolean = false): Promise<Template[]> => {
  const res = await httpClient.get<{ templates: Template[] }>('/v1/templates/', { params: { archived } })
  return res.data.templates
}

export const cloneTemplate = async (templateID: string): Promise<Template> => {
  const res = await httpClient.post<{ template: Template }>(`/v1/templates/${templateID}/clone`)
  return res.data.template
}

export const getTemplateVariables = async (templateID: string): Promise<TemplateVariable[]> => {
  const res = await httpClient.get<TemplateVariable[]>(`/v1/templates/${templateID}/template_variables`)
  return res.data
}

export const addTemplateVariable = async (templateID: string, templateVariable: TemplateVariable): Promise<TemplateVariable> => {
  const res = await httpClient.post<TemplateVariable>(`/v1/templates/${templateID}/template_variables`, {
    template_variable: prepareTemplateVariable(templateVariable),
  })
  return res.data
}

export const createTemplateFromExampleContract = async (name: string, file: File): Promise<AiRunResult> => {
  const body = new FormData()
  body.append('name', name)
  body.append('example_contract', file)
  const res = await httpClient.post<AiRunResult>(`/v1/ai_runs`, body, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

export const archiveTemplate = async (templateID: string): Promise<Template> => {
  const res = await httpClient.delete<Template>(`/v1/templates/${templateID}/archive`)
  return res.data
}

export const unarchiveTemplate = async (templateID: string): Promise<Template> => {
  const res = await httpClient.put<Template>(`/v1/templates/${templateID}/unarchive`)
  return res.data
}

export const getAiRun = async (aiRunId: string): Promise<AiRunResult> => {
  const res = await httpClient.get<AiRunResult>(`/v1/ai_runs/${aiRunId}`)
  return res.data
}

const prepareTemplateVariable = (templateVariable: TemplateVariable, originalTemplateVariable?: TemplateVariable) => {
  if (templateVariable.input_type === 'singleselect' || templateVariable.input_type === 'multiselect') {
    let selectOptionsAttributes: (SelectOption | DestroySelectOption)[] = [...templateVariable.select_options]

    if (
      originalTemplateVariable &&
      (originalTemplateVariable.input_type === 'singleselect' || originalTemplateVariable.input_type === 'multiselect')
    ) {
      const currentOptionIds = new Set(templateVariable.select_options.map(option => option.id).filter(Boolean))
      const removedOptions = originalTemplateVariable.select_options.filter(option => option.id && !currentOptionIds.has(option.id))

      const destroyAttributes = removedOptions.map(option => ({
        id: option.id!,
        _destroy: true,
      }))

      selectOptionsAttributes = [...selectOptionsAttributes, ...destroyAttributes]
    }

    return {
      ...templateVariable,
      select_options_attributes: selectOptionsAttributes,
    }
  }
  return templateVariable
}

export const updateTemplateVariable = async (
  templateID: string,
  templateVariable: TemplateVariable,
  originalTemplateVariable?: TemplateVariable
): Promise<TemplateVariable> => {
  if (!templateVariable.id) throw "No templatevariable ID set - can't update"
  const res = await httpClient.put<TemplateVariable>(`/v1/templates/${templateID}/template_variables/${templateVariable.id}`, {
    template_variable: prepareTemplateVariable(templateVariable, originalTemplateVariable),
  })
  return res.data
}

export const deleteTemplateVariable = async (templateID: string, templateVariableID: string): Promise<AxiosResponse> => {
  const res = await httpClient.delete(`/v1/templates/${templateID}/template_variables/${templateVariableID}`)

  return res
}

export const getSteps = async (templateID: string): Promise<ApiStep[]> => {
  const res = await httpClient.get<{ document_type: string; steps: ApiStep[] }>(`/v1/templates/${templateID}/steps`)
  const steps = res.data.steps

  return steps
}

export const updateStep = async (templateID: string, step: ApiStep): Promise<ApiStep> => {
  const res = await httpClient.put<ApiStep>(`/v1/templates/${templateID}/steps/${step.id}`, { step })

  return res.data
}

export const createStep = async (templateID: string, step: ApiStep): Promise<ApiStep> => {
  const res = await httpClient.post<ApiStep>(`/v1/templates/${templateID}/steps`, { step })

  return res.data
}

export const deleteStep = async (templateID: string, stepId: string): Promise<AxiosResponse> => {
  const res = await httpClient.delete<ApiStep>(`/v1/templates/${templateID}/steps/${stepId}`)

  return res
}

export const getQuestions = async (templateID: string): Promise<ApiQuestion[]> => {
  const res = await httpClient.get<ApiQuestion[]>(`/v1/templates/${templateID}/questions`)

  return res.data
}

export const createQuestion = async (templateID: string, question: ApiQuestion): Promise<ApiQuestion> => {
  const res = await httpClient.post<ApiQuestion>(`/v1/templates/${templateID}/questions`, { question })

  return res.data
}

export const updateQuestion = async (templateID: string, question: ApiQuestion): Promise<ApiQuestion> => {
  const res = await httpClient.put<ApiQuestion>(`/v1/templates/${templateID}/questions/${question.id}`, { question })

  return res.data
}

export const deleteQuestion = async (templateID: string, questionId: string): Promise<AxiosResponse> => {
  const res = await httpClient.delete(`/v1/templates/${templateID}/questions/${questionId}`)

  return res
}
